import { useSelector } from 'react-redux';
import { useCallback, useState, useEffect } from 'react';
import { Stack, Box, Grid, Drawer } from '@mui/material';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import 'react-toastify/dist/ReactToastify.css';
import { getMyBoard, getTaskById } from '../../../services/taskService';
import ProjectAddTask from '../../projects/details/components/projectTaskList/ProjectAddTask';
import Loading from '../../../layouts/loading';
import NavigationTab from './NavigationTab';
import { changeProjectOrder } from '../../../services/projectService';
import { fetchRunningTask } from '../../../services/trackerService';
import { Section } from '.';
import { isAdmin } from '../../../utils/share';
import { useStyles } from '../styles/sectionStyles';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';

// ----------------------------------------------------------------------

function BoardView() {
  const classes = useStyles();

  const [openModal, setModalOpen] = useState(false);
  const [action, setAction] = useState('');
  const [selected, setSelected] = useState();
  const [isLoading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [originalProjectList, setOriginalProjectList] = useState([]);
  const [runningTask, setRunningTask] = useState(null);
  const [refetcher, setRefetcher] = useState(false);
  const [allTask, setAllTask] = useState([]);

  const accountData = useSelector((state) => state?.login?.user);

  const handleRefetch = () => {
    setProjectList([]);
    setRefetcher((prev) => !prev);
  };

  const handleOpen = (project) => {
    // const projectId = projectId;
    setModalOpen(true);
    setSelectedProject(project);
  };
  const handleClose = () => setModalOpen(false);

  const handleAddTaskClick = useCallback((project) => {
    setAction('create');
    handleOpen({ name: project.projectName, id: project?.id });
  }, []);

  const handleTaskClick = useCallback(
    (task, project) => async () => {
      if (
        project?.projectmember?.projectRoles?.roleName === 'Team Members' &&
        task?.userId !== accountData?.id &&
        !isAdmin()
      ) {
        return;
      }

      setLoading(true);
      setAction('update');
      try {
        const response = await getTaskById(task.id);
        const taskData = response?.data?.data;
        if (taskData) {
          const updatedTaskData = {
            ...taskData,
            consumedHours: task.consumedHours,
          };

          setSelected(updatedTaskData);

          handleOpen({ name: task.projects.projectName, id: task?.projectId });
        } else {
          toast.error('Task data not found');
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    },
    [accountData?.id]
  );

  const onDragEnd = async (result) => {
    try {
      if (!result.destination) return;

      const reorderedProjectList = Array.from(projectList);
      const [movedProject] = reorderedProjectList.splice(result.source.index, 1);
      reorderedProjectList.splice(result.destination.index, 0, movedProject);

      const data = {
        sourceOrder: originalProjectList[result.source.index],
        destinationOrder: originalProjectList[result?.destination?.index],
      };

      setProjectList(reorderedProjectList);
      await changeProjectOrder(data);
    } catch (error) {
      toast.error('Something went wrong while reordering project list');
    }
  };

  useEffect(() => {
    const fetchMyProjectList = async () => {
      try {
        setLoading(true);
        const response = await getMyBoard();
        const projects = response?.data?.data;

        setOriginalProjectList(projects?.allProjects.map((project) => project.order));

        setProjectList(projects?.allProjects);
        setAllTask(projects?.allTasks);
      } catch (error) {
        const errorMessage = error?.response?.data?.message ?? 'Something went wrong while fetching project list';
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    const fetchRunningTaskData = async () => {
      const runningTaskResponse = await fetchRunningTask();
      setRunningTask(runningTaskResponse?.data?.data);
    };

    fetchMyProjectList();
    fetchRunningTaskData();
  }, [refetcher]);

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'My Task',
      path: '',
    },
  ];
  return (
    <>
      {/* <Loading open={isLoading} /> */}
      {/* <Box sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
          <BreadCrumb breadcrumbs={breadcrumbs} />
          <NavigationTab />
        </Stack>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="projects" direction="horizontal">
            {(provided) => (
              <Box className={classes?.rootStack} {...provided.droppableProps} ref={provided.innerRef}>
                {projectList.map((project, index) => (
                  <Draggable key={project.id} draggableId={project.id.toString()} index={index}>
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <Section
                          key={project.id}
                          section={project}
                          setLoading={setLoading}
                          runningTask={runningTask}
                          setRunningTask={setRunningTask}
                          handleTaskClick={handleTaskClick}
                          handleAddTaskClick={handleAddTaskClick}
                          allTask={allTask}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box> */}

      <Loading open={isLoading} />
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Fixed header area */}
        <Box mb={2.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <BreadCrumb breadcrumbs={breadcrumbs} />
            <NavigationTab />
          </Stack>
        </Box>

        {/* Scrollable project area */}
        <Box sx={{ flexGrow: 1, overflowX: 'auto', display: 'flex' }}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="projects" direction="horizontal">
              {(provided) => (
                <Box
                  className={classes?.rootStack}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: 'max-content',
                    '&::-webkit-scrollbar': {
                      height: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0,0,0,.2)',
                      borderRadius: '4px',
                    },
                  }}
                >
                  {projectList.map((project, index) => (
                    <Draggable key={project.id} draggableId={project.id.toString()} index={index}>
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            flexShrink: 0,
                            width: '300px',
                          }}
                        >
                          <Section
                            key={project.id}
                            section={project}
                            setLoading={setLoading}
                            runningTask={runningTask}
                            setRunningTask={setRunningTask}
                            handleTaskClick={handleTaskClick}
                            handleAddTaskClick={handleAddTaskClick}
                            allTask={allTask}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Box>

      <Grid>
        <Drawer
          PaperProps={{
            sx: {
              width: '55%',
            },
          }}
          anchor="right"
          open={openModal}
          onClose={handleClose}
        >
          <Box p={2}>
            <ProjectAddTask
              selectedTask={selected}
              open={openModal}
              project={selectedProject}
              close={handleClose}
              action={action}
              onSubmit={handleRefetch}
            />
          </Box>
        </Drawer>
      </Grid>
    </>
  );
}

export default BoardView;
